// F5 — **THE FUNNEL REPORT CARD, PER FUNNEL, UNDER HER ANALYTICS TAB.**
//
// Her clause: *"visits, submissions, submission rate, readings delivered, and conversions to signup
// and to PAID (email-matched) — the funnel report card, period dropdown, plain language."*
//
// ================================================================================================
// **IT READS THE DASHBOARD'S ONE RANGE AND HAS NO PERIOD CONTROL OF ITS OWN.**
//
// Her *"period dropdown"* is the one the Analytics tab already carries. D6-3's ruling is that two
// range controls on one dashboard are two answers to *this month*, so this component is HANDED the
// range query and never keeps one — and the server resolves it through `resolveRange`, the same law
// every other table on that tab is bounded by.
//
// **VISITS ARRIVED IN SURFACE 2(b), AND THIS BLOCK IS MIGRATED AT ITS DECLARATION RATHER THAN LEFT
// STANDING** (DL-2/DT-2b/R181 — a comment describing a retired state is read by the next session as
// the current one, and this repo's most-repeated instrument defect is an instrument still enforcing
// a superseded ruling).
//
// It used to read: *`funnel_leads` is written on SUBMIT, so a submission RATE has no denominator*.
// True, and still true of that table — what changed is that SURFACE 2(a)'s collector now records a
// visit per door (`site_visits.funnel`), so the denominator comes from the traffic ledger rather
// than from the leads table, exactly as her (b) clause asks.
//
// **WHAT SURVIVES UNCHANGED IS THE RULE THE OLD SENTENCE WAS REALLY ABOUT: PRINT WHAT IS KNOWN AND
// NAME WHAT IS NOT** (R17/R160, R141 — a number she makes ad spending decisions on with nothing
// under it is a false receipt). The collector counts FORWARD ONLY, so a window that begins before it
// started is `partial`: the visits are shown, **no rate is**, and the date is named. The payload
// carries `visitsState` and this reads it rather than assuming — so the sentence and the rate both
// change by themselves on the day the window is fully covered.
//
// **THE SIX-STATE READ IS R65's, NOT A TERNARY.** A payload in flight, a payload that arrived and
// could not be read, and a payload that arrived saying zero are three different facts (R18) — and
// only the third earns *"No funnel has been opened in this window."* Printing that over a database
// that refused the read is the one wrong thing to tell her about an advertisement she is paying for
// by the click.
// ================================================================================================
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote } from "../lib/owner-load";
import {
  ANALYTICS_DOORS, doorCapturesLead, doorTitle, funnelSubmitRate,
} from "../traffic.js";
import { OwnerBars } from "./OwnerBars";

/** One funnel's row, exactly as `funnelStats` returns it. */
export type FunnelStatRow = {
  funnel: string;
  submissions: number;
  named: number;
  readings: number;
  converted: number;
  paid: number;
  withPhone: number;
  unsubscribed: number;
};
/** SURFACE 2(b) — one door's visitors, as `funnelVisits` returns them. */
export type FunnelVisitRow = { funnel: string; visitors: number };
/** A door that captures no lead, as `landingDoorSignups` returns it. */
export type DoorSignupRow = { door: string; signups: number; paid: number };
/** A row of the table she reads: the leads half, the traffic half and — for a door with no lead row
 *  at all — the landing half, joined by door. `capturesLead` is carried ON the row rather than
 *  re-derived in the render, so the four columns that door cannot answer are decided ONCE. */
export type FunnelTableRow = FunnelStatRow & { visitors: number; capturesLead: boolean };
type FunnelStats = {
  range?: { key: string; label: string; days: number };
  visitsMeasured?: boolean;
  visitsState?: string;
  visitsSince?: string | null;
  visits?: FunnelVisitRow[];
  funnels?: FunnelStatRow[];
  doors?: DoorSignupRow[] | null;
  doorsState?: string;
  doorsSince?: string | null;
  /** HER ITEM 5 — the /talk chain in its own columns. `null` is a refused read, never a zero. */
  talkChain?: TalkChain | null;
  talkChainState?: string;
};

/** One rung of the /talk chain, as the server sends it. `counts` says WHO is being counted, because
 *  rung one is VISITORS and every rung below it is MEMBERS — a column header that hides that is the
 *  lie this whole card exists to end. */
export type TalkChainRung = { key: string; label: string; counts: string; n: number };
export type TalkChain = {
  rungs: TalkChainRung[];
  worst?: { from: string; to: string; label: string; lost: number; arrived: number; survived: number } | null;
  seam?: string;
};

/** An empty row for a door nobody came through, so all three funnels are always listed. A funnel
 *  missing from the table because nobody used it reads as a funnel that was never built — and the
 *  one thing she needs from this card is to see which door is not working (R43's M8: an absence and
 *  a zero look identical, and only one of them is an answer). */
const emptyRow = (funnel: string): FunnelTableRow => ({
  funnel, submissions: 0, named: 0, readings: 0, converted: 0, paid: 0, withPhone: 0, unsubscribed: 0,
  visitors: 0, capturesLead: doorCapturesLead(funnel),
});

/**
 * **THE THREE DOORS, ALWAYS, IN HER DECLARED ORDER — AND THIS IS A PURE FUNCTION SO A GATE RUNS IT.**
 *
 * The component around it holds two hooks and a fetch, which no fixture can call; this decision is
 * the one worth running, and it is the one that fails silently. `funnelStats` GROUPS BY funnel, so a
 * door nobody came through returns no row at all — and a table built straight off the payload would
 * simply not list it. **A funnel missing from her report card reads as a funnel that was never
 * built**, which is the opposite of what she needs from this screen: the whole point of a report card
 * is seeing which advertisement is not working (R43's M8 — an absence and a zero look identical, and
 * only one of them is an answer).
 */
export function funnelRows(
  rows: FunnelStatRow[] | null | undefined,
  visits: FunnelVisitRow[] | null | undefined = null,
  doors: DoorSignupRow[] | null | undefined = null,
  keys: string[] = ANALYTICS_DOORS,
): FunnelTableRow[] {
  const byKey = new Map((rows || []).map((f) => [f.funnel, f]));
  // SURFACE 2(b) — **THE TWO HALVES ARE JOINED BY DOOR HERE AND NOWHERE ELSE.** They come off two
  // different tables (`funnel_leads` and `site_visits`) and a door with traffic and no submissions
  // is the single most important row on this card — it is the advertisement that is being paid for
  // and converting nobody. Building the table off the leads rows alone would drop it entirely, which
  // is `emptyRow`'s own ruling one column over (R43's M8: an absence and a zero look identical, and
  // only one of them is an answer).
  const seen = new Map((visits || [])
    .filter((v) => v && typeof v.funnel === "string")
    .map((v) => [v.funnel, Number(v.visitors) || 0]));
  // >>> **AND THE THIRD HALF: A DOOR THAT CAPTURES NO LEAD ANSWERS THE SAME TWO COLUMNS FROM
  // `landing_tag` INSTEAD (2026-08-27).** <<< Her `/talk` door has no `funnel_leads` row in
  // existence, so *signed up* and *became members* come off `users` — and they land in the SAME two
  // fields, because they are the same two facts about the same journey and a second pair of columns
  // would be her asking which one to read.
  const landed = new Map((doors || [])
    .filter((d) => d && typeof d.door === "string")
    .map((d) => [d.door, d]));
  return keys.map((key) => {
    const capturesLead = doorCapturesLead(key);
    const base = { ...(byKey.get(key) || emptyRow(key)), visitors: seen.get(key) || 0, capturesLead };
    if (capturesLead) return base;
    const door = landed.get(key);
    return { ...base, converted: Number(door?.signups) || 0, paid: Number(door?.paid) || 0 };
  });
}

/**
 * **WHAT A COLUMN NOBODY ASKED SAYS, AND WHY IT IS NOT A DASH AND NEVER A ZERO.**
 *
 * Her `/talk` door captures no lead and mails no reading — there is no `funnel_leads` row behind it
 * in existence — so four of these eight columns have no answer for it. **A `0` there would read as a
 * door that is failing to convert**, on the screen she decides advertising spend from, which is the
 * one direction a wrong number here costs real money (R43's M8: an absence and a zero look identical
 * and only one of them is an answer).
 *
 * **AND IT IS NOT THE `—` EITHER, WHICH IS ALREADY TAKEN (R18/R28/R29).** That mark means *a rate
 * with no denominator* everywhere else on this table. Two different silences wearing one symbol is
 * two facts she cannot tell apart at a glance, so this one says what it means in words.
 */
const NOT_ASKED = "not asked";

/** A percentage of a real denominator, or nothing. **Never `0%` for a rate with no denominator** —
 *  *nobody converted* and *nobody arrived* are different facts and only the first is a problem. */
export function ratePct(part: number, whole: number): string {
  const p = Number(part) || 0;
  const w = Number(whole) || 0;
  if (w <= 0) return "—";
  return `${Math.round((p / w) * 100)}%`;
}

export function OwnerFunnels({ rangeQuery, onDenied }: { rangeQuery: string; onDenied: () => void }) {
  const [data, setData] = useState<FunnelStats | null>(null);
  const [note, setNote] = useState("");
  const [read, setRead] = useState(false);

  const load = useCallback(() => {
    loadOwnerData<FunnelStats>(`/api/funnel/stats?${rangeQuery}`)
      .then((answer) => {
        if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
        if (answer.ok) setData(answer.data);
        setNote(ownerLoadNote(answer));
        setRead(true);
      })
      .catch(() => { setNote("These numbers could not be read just now."); setRead(true); });
  }, [rangeQuery, onDenied]);

  useEffect(() => { load(); }, [load]);

  // The three doors ALWAYS, in her own declared order, each carrying whatever the table had for it.
  const rows = funnelRows(data?.funnels, data?.visits, data?.doors);
  // **ONLY THE DOORS THAT ASK FOR DETAILS COUNT TOWARDS *gave their details*.** A door that never
  // asks contributes a truthful nothing here, and folding it in would make the sentence a total of
  // two different questions.
  const total = rows.filter((r) => r.capturesLead).reduce((sum, r) => sum + r.submissions, 0);
  const doorsState = data?.doorsState || "unmeasured";
  const talkChain = data?.talkChain || null;
  const talkChainState = data?.talkChainState || "unmeasured";
  // SURFACE 2(b). The state decides whether a RATE may be drawn at all, and it is the payload's own
  // — never re-derived here from `visits.length`, which would call a genuinely quiet window
  // "unmeasured" and a fully-covered one with no traffic "measured" (R28/R29: one answer, and the
  // one that drifts is the one nobody checks).
  const visitsState = data?.visitsState || "unmeasured";

  return (
    <div className="owner-funnels">
      <h3 className="owner-detail__sub">Her funnels</h3>
      <p className="owner-page__note owner-funnels__line">
        {!read
          ? "…"
          : !data
            ? "These numbers could not be read just now."
            : total === 0
              ? "Nobody has finished a funnel in this window."
              : `${total.toLocaleString("en-US")} ${total === 1 ? "person" : "people"} gave their details in this window.`}
      </p>
      {/* HER MISSING HALF, PRINTED — AND IT IS THREE SENTENCES NOW BECAUSE IT IS THREE FACTS (R18).
          It reads the payload's own state rather than being a sentence somebody has to remember to
          delete, so each one goes away by itself the day it stops being true.

          `measured` says NOTHING, which is the point: a screen that keeps apologising after the gap
          has closed is a screen she stops reading. */}
      {read && data && visitsState === "unmeasured" ? (
        <p className="owner-page__note owner-funnels__gap">
          Page visits are not counted yet, so there is no submission rate here. These are the people
          who finished.
        </p>
      ) : null}
      {read && data && visitsState === "partial" ? (
        <p className="owner-page__note owner-funnels__gap">
          Visits have only been counted since {String(data.visitsSince || "").slice(0, 10)}, which is
          inside this window — so the visitor counts below are short and there is no submission rate
          yet. Pick a shorter period to see one.
        </p>
      ) : null}
      {/* **THE ROW THAT CANNOT ANSWER FOUR OF THESE COLUMNS SAYS SO ONCE, IN HER WORDS**, rather
          than making her work out what *not asked* means four times over. It is drawn only when such
          a door is really in the table, so it goes away by itself if that ever stops being true. */}
      {read && data && rows.some((r) => !r.capturesLead) ? (
        <p className="owner-page__note owner-funnels__gap">
          Ask Lala is a door, not a form: it never asks for details and never mails a reading, so
          those four columns say <em>not asked</em> rather than nought. What it can be judged on is
          the three that matter — who saw it, who signed up, and who became a member.
        </p>
      ) : null}
      {/* And the same honest boundary the visits half carries, over the other counter. */}
      {read && data && rows.some((r) => !r.capturesLead) && doorsState === "partial" ? (
        <p className="owner-page__note owner-funnels__gap">
          Ask Lala signups have only been attributed since{" "}
          {String(data.doorsSince || "").slice(0, 10)}, which is inside this window — so that row's
          signups are short of the real number. Pick a shorter period to see the whole of it.
        </p>
      ) : null}
      {note ? <p className="owner-page__note">{note}</p> : null}

      <div className="owner-scroll">
        <table className="owner-table owner-funnels__table">
          <thead>
            <tr>
              <th>Funnel</th>
              {/* SURFACE 2(b) — her (b) clause names VISITS first, and it is first here for the
                  reason her order puts it there: it is the top of the journey and every rate under
                  it is a share of this number. */}
              <th>Saw the page</th>
              <th>Gave details</th>
              <th>Got a reading</th>
              <th>Left a phone</th>
              <th>Signed up</th>
              <th>Became members</th>
              <th>Off the list</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.funnel}>
                {/* Her own headline for that door, so the row says which ADVERTISEMENT it is about
                    rather than which key somebody typed (R11/R31 — one declaration, and the ad, the
                    page and this row read the same string). */}
                {/* `doorTitle` and not `funnelCopy`: that resolver FALLS BACK to `wealthy` for
                    anything off its roster, so the moment her `/talk` door joined this table it
                    would have carried another door's advertisement as its name — on the one screen
                    whose whole job is telling her doors apart. */}
                <td data-label="Funnel">{doorTitle(r.funnel)}</td>
                <td data-label="Saw the page">{r.visitors.toLocaleString("en-US")}</td>
                {/* THE SUBMISSION RATE, AND IT IS ONLY EVER DRAWN WHEN THE DENOMINATOR COVERS THE
                    WINDOW. `funnelSubmitRate` answers null for `partial` and for `unmeasured`, so
                    there is no branch here that can put a flattering fraction on her screen. */}
                <td data-label="Gave details">
                  {r.capturesLead ? (
                    <>
                      {r.submissions.toLocaleString("en-US")}
                      {funnelSubmitRate(r.submissions, r.visitors, visitsState) === null
                        ? null
                        : <em>{funnelSubmitRate(r.submissions, r.visitors, visitsState)}%</em>}
                    </>
                  ) : NOT_ASKED}
                </td>
                <td data-label="Got a reading">{r.capturesLead ? r.readings.toLocaleString("en-US") : NOT_ASKED}</td>
                <td data-label="Left a phone">{r.capturesLead ? r.withPhone.toLocaleString("en-US") : NOT_ASKED}</td>
                {/* **THE DENOMINATOR IS THE STEP BEFORE, AND FOR A DOOR WITH NO FORM THAT STEP IS THE
                    VISIT.** A door that never asks for details has no submissions to divide by, and
                    dividing by them anyway would print `—` beside a real, working number. */}
                <td data-label="Signed up">
                  {r.converted.toLocaleString("en-US")}{" "}
                  {r.capturesLead
                    ? <em>{ratePct(r.converted, r.submissions)}</em>
                    : (funnelSubmitRate(r.converted, r.visitors, visitsState) === null
                      ? null
                      : <em>{funnelSubmitRate(r.converted, r.visitors, visitsState)}%</em>)}
                </td>
                <td data-label="Became members">
                  {r.paid.toLocaleString("en-US")}{" "}
                  <em>{ratePct(r.paid, r.capturesLead ? r.submissions : r.converted)}</em>
                </td>
                <td data-label="Off the list">{r.capturesLead ? r.unsubscribed.toLocaleString("en-US") : NOT_ASKED}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* >>> **HER ITEM 5 — THE ASK LALA CHAIN, IN THE COLUMNS THAT DOOR ACTUALLY HAS.** <<<
          Her words: *"so she can finally SEE where people die instead of guessing."* The table above
          answers four of its columns with *not asked*, correctly, because those are questions of a
          lead form and this door has none — and that silence is what let 24,852 women be lost
          between seeing the page and having an account while nothing on this screen moved.

          **THE DROP IS DRAWN, NOT LEFT AS SUBTRACTION.** A column of nine numbers still asks her to
          work out where the wound is, which is the guessing her order is ending. */}
      {read && talkChain && talkChain.rungs?.length ? (
        <div className="owner-funnels__chain">
          <h3 className="owner-funnels__chain-title">Ask Lala, step by step</h3>
          {talkChain.worst ? (
            <p className="owner-page__note owner-funnels__wound">
              The biggest drop is into <strong>{talkChain.worst.label}</strong>:{" "}
              <strong>{talkChain.worst.lost.toLocaleString("en-US")}</strong> lost there.{" "}
              {talkChain.worst.arrived.toLocaleString("en-US")} reached the step above it and{" "}
              {talkChain.worst.survived.toLocaleString("en-US")} came out.
            </p>
          ) : null}
          <div className="owner-scroll">
            <table className="owner-table owner-funnels__chain-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>People</th>
                  <th>Share</th>
                  <th>Lost here</th>
                </tr>
              </thead>
              <tbody>
                {talkChain.rungs.map((rung, i) => {
                  const top = talkChain.rungs[0]?.n || 0;
                  const prev = i === 0 ? null : talkChain.rungs[i - 1].n;
                  // A rung that goes UP is drawn as a dash, never a negative: the populations change
                  // at the first step, and a negative loss on her screen is a board she stops
                  // trusting. The seam is named in the footnote rather than hidden in a minus sign.
                  const lost = prev === null || prev - rung.n <= 0 ? null : prev - rung.n;
                  return (
                    <tr key={rung.key}>
                      <td data-label="Step">{rung.label}</td>
                      <td data-label="People">{rung.n.toLocaleString("en-US")}</td>
                      <td data-label="Share">
                        {top ? <em>{((rung.n / top) * 100).toFixed(2)}%</em> : NOT_ASKED}
                      </td>
                      <td data-label="Lost here">
                        {lost === null ? "—" : lost.toLocaleString("en-US")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* The seam, said out loud. Rung one is a different population from the rest, and a card
              that let her read the first drop as a product failure would be lying in a new way. */}
          <p className="owner-page__note owner-funnels__gap">
            {talkChain.seam
              || "The first step counts visitors; every step below it counts members this house can name."}{" "}
            {talkChainState === "unreadable"
              ? "This reading could not be taken just now."
              : null}
          </p>
        </div>
      ) : null}

      {/* **THE BARS COMPARE *gave their details*, SO ONLY THE DOORS THAT ASK ARE ON THEM.** A door
          with no form drawn at zero beside three that have one is a chart saying it lost, and it was
          never in that race. */}
      <OwnerBars
        rows={rows.filter((r) => r.capturesLead)
          .map((r) => ({ key: r.funnel, label: doorTitle(r.funnel), value: r.submissions }))}
        empty="No funnel has been opened in this window."
      />
    </div>
  );
}
